import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, CircleNotch, PencilSimple, Plus, Tag, Trash, X } from '@phosphor-icons/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TagManagement = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingTagId, setSavingTagId] = useState(null);
  const [deletingTagId, setDeletingTagId] = useState(null);
  const [tagToDelete, setTagToDelete] = useState(null);

  const fetchTags = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tags`);
      setTags(data);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('Failed to load tags');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const addTag = async () => {
    if (!newTag.trim()) {
      toast.error('Enter a tag name');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tags`, { name: newTag.trim() });
      toast.success('Tag added');
      setNewTag('');
      await fetchTags(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add tag');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteTag = async (event) => {
    event.preventDefault();
    const tag = tagToDelete;
    if (!tag || savingTagId || deletingTagId) return;
    setDeletingTagId(tag.id);
    try {
      await axios.delete(`${API}/tags/${tag.id}`);
      toast.success('Tag deleted');
      await fetchTags(false);
      setTagToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tag');
    } finally {
      setDeletingTagId(null);
    }
  };

  const beginEdit = (tag) => {
    setEditingTagId(tag.id);
    setEditingName(tag.name);
  };

  const cancelEdit = () => {
    setEditingTagId(null);
    setEditingName('');
  };

  const saveEdit = async (tag) => {
    if (savingTagId || deletingTagId) return;
    if (!editingName.trim()) {
      toast.error('Tag name is required');
      return;
    }
    setSavingTagId(tag.id);
    try {
      await axios.patch(`${API}/tags/${tag.id}`, { name: editingName.trim() });
      toast.success('Tag name updated');
      await fetchTags(false);
      cancelEdit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tag');
    } finally {
      setSavingTagId(null);
    }
  };

  const formatCreatedAt = (value) => {
    if (!value) return 'Unknown';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? 'Unknown'
      : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  };

  return (
    <main className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="tag-management-page">
      <div className="max-w-4xl mx-auto">
        <header className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Tag Management
          </h1>
          <p className="text-base text-slate-600 leading-relaxed mt-2">
            Create and manage reusable tags.
          </p>
        </header>

        <section className="bg-white border border-slate-200 rounded-lg p-6" aria-label="Tags">
          <form
            className="flex items-center gap-3 mb-5"
            onSubmit={(event) => { event.preventDefault(); addTag(); }}
          >
            <input
              type="text"
              value={newTag}
              onChange={(event) => setNewTag(event.target.value.slice(0, 100))}
              placeholder="Enter a tag name"
              aria-label="New tag name"
              className="flex-1 min-w-0 px-4 py-2.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="tag-name-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 btn-primary px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"
              data-testid="tag-add-button"
            >
              <Plus size={18} weight="bold" />
              <span>Add tag</span>
            </button>
          </form>

          {loading ? (
            <div className="space-y-2" aria-label="Loading tags">
              {[1, 2, 3].map((index) => <div key={index} className="h-11 bg-slate-100 rounded-md skeleton" />)}
            </div>
          ) : tags.length === 0 ? (
            <div className="text-center py-10" data-testid="tag-empty-state">
              <Tag size={40} weight="duotone" className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No tags added yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="tag-list">
              <table className="w-full min-w-[620px] text-left">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Created by</th>
                    <th className="px-4 py-3 font-medium">Created at</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag) => (
                    <tr key={tag.id} className="border-b border-slate-100 last:border-0" data-testid={`tag-item-${tag.id}`}>
                      <td className="px-4 py-3">
                        {editingTagId === tag.id ? (
                          <input
                            autoFocus
                            type="text"
                            value={editingName}
                            maxLength={100}
                            onChange={(event) => setEditingName(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') saveEdit(tag);
                              if (event.key === 'Escape') cancelEdit();
                            }}
                            disabled={savingTagId === tag.id}
                            aria-label="Edit tag name"
                            className="w-full max-w-sm px-3 py-2 bg-white text-slate-800 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                            data-testid={`tag-edit-input-${tag.id}`}
                          />
                        ) : (
                          <span className="text-sm font-medium text-slate-800">{tag.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{tag.created_by || 'Unknown'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{formatCreatedAt(tag.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {editingTagId === tag.id ? (
                            <>
                              <button
                                type="button"
                                onClick={() => saveEdit(tag)}
                                disabled={Boolean(savingTagId || deletingTagId)}
                                className="p-2 text-emerald-700 hover:bg-emerald-50 rounded transition-colors"
                                title="Save name"
                                aria-label={`Save ${tag.name}`}
                                data-testid={`tag-save-${tag.id}`}
                              >
                                {savingTagId === tag.id
                                  ? <CircleNotch size={17} className="animate-spin" />
                                  : <Check size={17} weight="bold" />}
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                disabled={Boolean(savingTagId || deletingTagId)}
                                className="p-2 text-slate-500 hover:bg-slate-100 rounded transition-colors"
                                title="Cancel edit"
                                aria-label={`Cancel editing ${tag.name}`}
                              >
                                <X size={17} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => beginEdit(tag)}
                                disabled={Boolean(savingTagId || deletingTagId)}
                                className="p-2 text-slate-500 hover:text-[#002FA7] hover:bg-slate-100 rounded transition-colors"
                                title="Edit name"
                                aria-label={`Edit ${tag.name}`}
                                data-testid={`tag-edit-${tag.id}`}
                              >
                                <PencilSimple size={17} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setTagToDelete(tag)}
                                disabled={Boolean(savingTagId || deletingTagId)}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title={`Delete ${tag.name}`}
                                aria-label={`Delete ${tag.name}`}
                                data-testid={`tag-delete-${tag.id}`}
                              >
                                {deletingTagId === tag.id ? (
                                  <CircleNotch size={16} className="animate-spin" />
                                ) : (
                                  <Trash size={16} />
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <AlertDialog
          open={Boolean(tagToDelete)}
          onOpenChange={(open) => {
            if (!open && !deletingTagId) setTagToDelete(null);
          }}
        >
          <AlertDialogContent className="border border-slate-700 bg-[#1a0d38] text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete tag?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-300">
                {tagToDelete
                  ? `Delete "${tagToDelete.name}"? This action cannot be undone.`
                  : 'This action cannot be undone.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                disabled={Boolean(deletingTagId)}
                className="border-slate-600 bg-transparent text-white hover:bg-white/10"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteTag}
                disabled={Boolean(deletingTagId)}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {deletingTagId && <CircleNotch size={16} className="animate-spin" />}
                {deletingTagId ? 'Deleting...' : 'Delete tag'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </main>
  );
};

export default TagManagement;